import React from "react";

import IconButton from "../button/IconButton";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import { toMarkdownLink } from "../../utils/deepLink";

/**
 * ディープリンクの「リンクをコピー」ボタン。
 *
 * IconButton（icon="link"）+ useCopyToClipboard の薄いラッパー。
 * label を渡すと Markdown リンク `[label](url)` 形式でコピーし、トークルーム /
 * ショットコメント / DM に貼ったときにタイトル付きで表示される
 * （MarkdownContent が内部リンクとして SPA 内遷移させる）。
 *
 * トースト表示には呼び出し側ツリーで `<Toaster />`（SoremoToaster）が
 * マウントされている必要がある（App.tsx でグローバルに mount 済み）。
 */

export interface CopyLinkButtonProps {
  /** コピーする URL。クリック時点の状態から組み立てる場合は関数を渡す。 */
  url: string | (() => string);
  /** Markdown リンクのラベル。指定時は `[label](url)` 形式でコピーする。 */
  label?: string;
  /** アイコンサイズ。 */
  size?: "small" | "medium" | "large";
  /** ツールチップ / aria-label の文言。既定「リンクをコピー」。 */
  title?: string;
  disabled?: boolean;
  className?: string;
}

const SIZE_PX: Record<NonNullable<CopyLinkButtonProps["size"]>, number> = {
  small: 16,
  medium: 20,
  large: 24,
};

const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({
  url,
  label,
  size = "medium",
  title = "リンクをコピー",
  disabled = false,
  className,
}) => {
  const { copy } = useCopyToClipboard({
    successMessage: "リンクをコピーしました",
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // 行クリック（選択・ナビゲーション）と重ねて置かれるため伝播を止める
    e.stopPropagation();
    const resolved = typeof url === "function" ? url() : url;
    void copy(label ? toMarkdownLink(label, resolved) : resolved);
  };

  return (
    <IconButton
      icon="link"
      size={SIZE_PX[size]}
      title={title}
      ariaLabel={title}
      disabled={disabled}
      className={className}
      onClick={handleClick}
    />
  );
};

export default CopyLinkButton;
